import { useEffect, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import leadService, {
  LEAD_STATUS_GROUPS, LEAD_STATUS_LABELS, LEAD_STATUS_BADGE, chipColor, ACTIVITY_LABELS,
  CALL_OUTCOME_LABELS,
} from '../../services/leadService'
import teamService from '../../services/teamService'
import formService from '../../services/formService'
import { useAuthStore } from '../../store/authStore'
import {
  HiOutlineSearch, HiOutlineX, HiOutlinePhone, HiOutlineMail,
  HiOutlineTrash, HiOutlineDocumentText, HiOutlineUserAdd, HiOutlineClock,
  HiStar, HiOutlineStar, HiOutlineArrowLeft, HiOutlineAdjustments,
} from 'react-icons/hi'
import toast from 'react-hot-toast'

const MANAGER_ROLES = ['admin', 'hr_manager', 'team_lead']

function StatusSelect({ value, onChange, className = 'select', includeAll = false }) {
  return (
    <select className={className} value={value} onChange={onChange}>
      {includeAll && <option value="">Tất cả trạng thái</option>}
      {LEAD_STATUS_GROUPS.map((g) => (
        <optgroup key={g.key} label={g.label}>
          {g.statuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </optgroup>
      ))}
    </select>
  )
}

const fmtDateTime = (s) => (s ? new Date(s).toLocaleString('vi-VN', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  hour12: false, timeZone: 'Asia/Ho_Chi_Minh',
}) : '—')

// Thời gian tương đối (vừa xong / 3 giờ trước / 2 ngày trước), quá 7 ngày -> ngày đầy đủ
const fmtRelative = (s) => {
  if (!s) return '—'
  const diff = (Date.now() - new Date(s).getTime()) / 1000
  if (diff < 60) return 'vừa xong'
  if (diff < 3600) return `${Math.floor(diff / 60)} phút trước`
  if (diff < 86400) return `${Math.floor(diff / 3600)} giờ trước`
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} ngày trước`
  return fmtDateTime(s)
}

// Dãy số trang có dấu … (vd: 1 … 4 5 6 … 20)
function pageNumbers(cur, total) {
  if (!total || total <= 7) return Array.from({ length: total || 0 }, (_, i) => i + 1)
  const keep = [...new Set([1, total, cur, cur - 1, cur + 1])].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b)
  const out = []
  let prev = 0
  for (const n of keep) { if (n - prev > 1) out.push('…'); out.push(n); prev = n }
  return out
}

function AssigneeChip({ name }) {
  if (!name) return <span className="text-secondary-300">Chưa giao</span>
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${chipColor(name)}`}>{name}</span>
}

// Mở/tải CV qua API có xác thực. Mở tab trống TRƯỚC khi await để tránh bị popup-blocker chặn.
async function openCv(lead) {
  const win = window.open('', '_blank')
  try {
    const blob = await leadService.downloadCvBlob(lead.id)
    const url = URL.createObjectURL(blob)
    if (win) win.location = url
    else {
      const a = document.createElement('a')
      a.href = url; a.download = lead.cv_filename || 'cv'
      document.body.appendChild(a); a.click(); a.remove()
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000)
  } catch {
    if (win) win.close()   // interceptor đã hiển thị lỗi
  }
}

function LeadManagement() {
  const { user } = useAuthStore()
  const canAssign = MANAGER_ROLES.includes(user?.role)
  const isAdmin = user?.role === 'admin'
  // Xóa được: quản lý/trưởng nhóm; HOẶC nhân viên chủ chiến dịch xóa lead từ chiến dịch mình tạo
  const canDeleteLead = (lead) => canAssign || (!!lead?.form && lead.form.created_by === user?.id)

  // Khởi tạo bộ lọc từ URL (drill-down từ Tổng quan: ?group=, ?source=, ?region=, ?assigned_to=, ?range=, ?status=)
  const qp = new URLSearchParams(window.location.search)
  const [leads, setLeads] = useState([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState(qp.get('status') || '')
  const [groupFilter, setGroupFilter] = useState(qp.get('group') || '')
  const [formFilter, setFormFilter] = useState(qp.get('form_id') || '')
  const [assigneeFilter, setAssigneeFilter] = useState(qp.get('assigned_to') || '')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState(null)
  const [assignables, setAssignables] = useState([])
  const [formList, setFormList] = useState([])
  const [picked, setPicked] = useState(() => new Set())
  const [bulkTarget, setBulkTarget] = useState('')
  const [sourceFilter, setSourceFilter] = useState(qp.get('source') || '')
  const [regionFilter, setRegionFilter] = useState(qp.get('region') || '')
  const [pinnedFilter, setPinnedFilter] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)   // mobile: mở bộ lọc chi tiết
  const [filterOpts, setFilterOpts] = useState({ sources: [], regions: [] })
  const [range, setRange] = useState(qp.get('range') || '')        // '' = tất cả thời gian
  const [sort, setSort] = useState('smart')     // smart | newest | updated
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState(null)
  const PER_PAGE = 20

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const params = { page, per_page: PER_PAGE, sort }
      if (statusFilter) params.status = statusFilter
      if (groupFilter) params.group = groupFilter
      if (formFilter) params.form_id = formFilter
      if (assigneeFilter) params.assigned_to = assigneeFilter
      if (sourceFilter) params.source = sourceFilter
      if (regionFilter) params.region = regionFilter
      if (pinnedFilter) params.pinned = 1
      if (range) params.range = range
      if (dateFrom) params.date_from = dateFrom
      if (dateTo) params.date_to = dateTo
      if (search.trim()) params.search = search.trim()
      const res = await leadService.list(params)
      if (res.status === 'success') { setLeads(res.data); setPagination(res.pagination || null) }
      setPicked(new Set())
    } finally {
      setLoading(false)
    }
  }, [statusFilter, groupFilter, formFilter, assigneeFilter, sourceFilter, regionFilter, pinnedFilter, range, dateFrom, dateTo, search, sort, page])

  // Đổi bộ lọc -> về trang 1 (debounce của effect load sẽ gộp lại 1 lần gọi)
  useEffect(() => { setPage(1) }, [statusFilter, groupFilter, formFilter, assigneeFilter, sourceFilter, regionFilter, pinnedFilter, range, dateFrom, dateTo, search, sort])

  useEffect(() => {
    leadService.filterOptions().then((r) => { if (r.status === 'success') setFilterOpts(r.data) }).catch(() => {})
  }, [])

  useEffect(() => {
    const t = setTimeout(load, 300)
    return () => clearTimeout(t)
  }, [load])

  useEffect(() => {
    if (canAssign) {
      teamService.assignableUsers()
        .then((res) => { if (res.status === 'success') setAssignables(res.data) })
        .catch(() => {})
    }
    if (canAssign) {
      formService.list()
        .then((res) => { if (res.status === 'success') setFormList(res.data) })
        .catch(() => {})
    }
  }, [canAssign, isAdmin])

  // Deep-link từ email: /admin/leads?open=<id>[&cv=1] -> mở drawer đúng lead (+ xem CV nếu cv=1)
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    const openId = searchParams.get('open')
    if (!openId) return
    const wantCv = searchParams.get('cv') === '1'
    leadService.get(openId)
      .then((res) => {
        if (res.status !== 'success') return
        setSelected(res.data)
        if (wantCv && res.data.cv_url) openCv(res.data)
      })
      .catch(() => {})
      .finally(() => setSearchParams({}, { replace: true }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const togglePin = async (lead, e) => {
    if (e) e.stopPropagation()
    const willPin = !lead.is_pinned
    setLeads((arr) => arr.map((l) => (l.id === lead.id ? { ...l, is_pinned: willPin } : l)))
    setSelected((s) => (s && s.id === lead.id ? { ...s, is_pinned: willPin } : s))
    try {
      if (willPin) await leadService.pin(lead.id)
      else await leadService.unpin(lead.id)
      if (pinnedFilter && !willPin) load()   // đang lọc ghim -> bỏ ghim thì reload cho biến mất
    } catch {
      setLeads((arr) => arr.map((l) => (l.id === lead.id ? { ...l, is_pinned: !willPin } : l)))
    }
  }

  const handleDelete = async (lead) => {
    // Xóa được ghi LỊCH SỬ (ai/khi nào/lý do) để chống gian lận — nhắc lý do (tùy chọn)
    const reason = window.prompt(
      `Xóa hồ sơ "${lead.full_name}" (${lead.phone})?\nHành động này được ghi lại. Nhập lý do (có thể để trống rồi OK):`,
      ''
    )
    if (reason === null) return   // bấm Cancel
    try {
      await leadService.remove(lead.id, reason.trim())
      toast.success('Đã xóa hồ sơ (đã ghi lịch sử)'); setSelected(null); load()
    } catch { /* interceptor */ }
  }

  const togglePick = (id) => setPicked((p) => {
    const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n
  })
  const toggleAll = () => setPicked((p) => p.size === leads.length ? new Set() : new Set(leads.map((l) => l.id)))

  const doBulkAssign = async () => {
    if (!bulkTarget) { toast.error('Chọn người nhận'); return }
    try {
      const res = await leadService.assign([...picked], Number(bulkTarget))
      if (res.status === 'success') { toast.success(res.message || 'Đã phân công'); setBulkTarget(''); load() }
    } catch { /* interceptor */ }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="page-title">Hồ sơ ứng viên</h1>
          <p className="page-subtitle">Danh sách lead tuyển dụng — lọc, phân luồng & ghi chú</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-3 sm:p-4 mb-4 space-y-3">
        {/* Hàng 1: tìm kiếm + nút Lọc (mobile) — tab nhóm funnel cuộn ngang */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="flex items-center gap-2 sm:order-2">
            <div className="relative flex-1 sm:flex-none">
              <HiOutlineSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400 pointer-events-none" />
              <input className="input pl-8 pr-7 w-full sm:w-56 md:w-64" placeholder="Tìm ID/tên/SĐT/email…"
                value={search} onChange={(e) => setSearch(e.target.value)} />
              {search && <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-700"><HiOutlineX className="w-4 h-4" /></button>}
            </div>
            <button onClick={() => setFiltersOpen((v) => !v)}
              className={`md:hidden shrink-0 px-3 py-2.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1 ${filtersOpen ? 'bg-primary-600 text-white' : 'bg-secondary-100 text-secondary-600'}`}>
              <HiOutlineAdjustments className="w-4 h-4" /> Lọc
            </button>
          </div>
          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-thin -mx-0.5 px-0.5 sm:flex-1 sm:order-1">
            <button onClick={() => { setGroupFilter(''); setStatusFilter('') }}
              className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold ${!groupFilter && !statusFilter ? 'bg-primary-600 text-white' : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'}`}>Tất cả</button>
            {LEAD_STATUS_GROUPS.map((g) => (
              <button key={g.key} onClick={() => { setGroupFilter(g.key); setStatusFilter('') }}
                className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold ${groupFilter === g.key ? 'bg-primary-600 text-white' : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'}`}>{g.label}</button>
            ))}
            <button onClick={() => setPinnedFilter((v) => !v)} title="Ứng viên tôi ghim"
              className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1 ${pinnedFilter ? 'bg-amber-500 text-white' : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'}`}>
              <HiStar className="w-3.5 h-3.5" /> Ghim
            </button>
          </div>
        </div>

        <div className={`space-y-3 ${filtersOpen ? '' : 'hidden'} md:block`}>
        {/* Hàng 2: người liên hệ + nguồn + khu vực + chiến dịch + trạng thái */}
        <div className="flex flex-wrap items-center gap-2">
          {canAssign && (
            <select className="select flex-1 sm:flex-none sm:w-auto text-sm min-w-[46%] sm:min-w-[150px]" value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)}>
              <option value="">👥 Tất cả người liên hệ</option>
              <option value="unassigned">⚠️ Chưa giao</option>
              {assignables.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          )}
          <select className="select flex-1 sm:flex-none sm:w-auto text-sm min-w-[46%] sm:min-w-[130px]" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
            <option value="">📡 Tất cả nguồn</option>
            {filterOpts.sources.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="select flex-1 sm:flex-none sm:w-auto text-sm min-w-[46%] sm:min-w-[130px]" value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)}>
            <option value="">📍 Tất cả khu vực</option>
            {filterOpts.regions.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          {canAssign && (
            <select className="select flex-1 sm:flex-none sm:w-auto text-sm min-w-[46%] sm:min-w-[130px]" value={formFilter} onChange={(e) => setFormFilter(e.target.value)}>
              <option value="">Tất cả chiến dịch</option>
              <option value="landing">Trang chủ (form chung)</option>
              {formList.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
            </select>
          )}
          <StatusSelect includeAll className="select flex-1 sm:flex-none sm:w-auto text-sm min-w-[46%] sm:min-w-0 sm:ml-auto" value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); if (e.target.value) setGroupFilter('') }} />
        </div>

        {/* Hàng 3: thời gian + sắp xếp */}
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-secondary-50">
          {[['', 'Tất cả'], ['today', 'Hôm nay'], ['yesterday', 'Hôm qua'], ['7d', '7N'], ['14d', '14N'], ['30d', '30N'], ['month', 'Tháng này']].map(([v, label]) => (
            <button key={v || 'all'} type="button" onClick={() => setRange(v)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${range === v ? 'bg-accent-500 text-white border-accent-500' : 'bg-white text-secondary-600 border-secondary-200 hover:border-accent-400'}`}>{label}</button>
          ))}
          <input type="date" className="input w-36 text-xs" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} title="Từ ngày" />
          <input type="date" className="input w-36 text-xs" value={dateTo} onChange={(e) => setDateTo(e.target.value)} title="Đến ngày" />
          <select className="select w-auto text-xs ml-auto" value={sort} onChange={(e) => setSort(e.target.value)} title="Sắp xếp">
            <option value="smart">⚡ Ưu tiên chưa xử lý</option>
            <option value="newest">🕒 Mới nhất</option>
            <option value="updated">✏️ Cập nhật gần nhất</option>
          </select>
        </div>
        </div>
      </div>

      {/* Bulk assign bar */}
      {canAssign && picked.size > 0 && (
        <div className="card p-3 mb-4 flex flex-wrap items-center gap-3 bg-primary-50/50 border-primary-100">
          <span className="text-sm font-medium text-primary-700">Đã chọn {picked.size} hồ sơ</span>
          <select className="select w-auto" value={bulkTarget} onChange={(e) => setBulkTarget(e.target.value)}>
            <option value="">— Phân cho nhân viên —</option>
            {assignables.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
          <button onClick={doBulkAssign} className="btn-primary btn-sm"><HiOutlineUserAdd className="w-4 h-4" /> Phân luồng</button>
          <button onClick={() => setPicked(new Set())} className="btn-ghost btn-sm">Bỏ chọn</button>
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><div className="spinner w-8 h-8" /></div>
        ) : leads.length === 0 ? (
          <div className="text-center py-16 text-secondary-400">Không có hồ sơ nào khớp bộ lọc</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-2xs uppercase tracking-wide text-secondary-400 border-b border-secondary-100 bg-secondary-50/60">
                  {canAssign && (
                    <th className="pl-3 pr-1 py-3"><input type="checkbox" checked={picked.size === leads.length && leads.length > 0} onChange={toggleAll} /></th>
                  )}
                  <th className="px-1 py-3"><HiStar className="w-3.5 h-3.5" /></th>
                  <th className="px-3 py-3 whitespace-nowrap hidden sm:table-cell">Thời gian</th>
                  <th className="px-2 py-3 hidden sm:table-cell">ID</th>
                  <th className="px-3 py-3">Ứng viên</th>
                  <th className="px-3 py-3 hidden md:table-cell">Nguồn · Khu vực · Vị trí</th>
                  {canAssign && <th className="px-3 py-3 hidden lg:table-cell">Người liên hệ</th>}
                  <th className="px-3 py-3">Trạng thái</th>
                  <th className="px-3 py-3 hidden lg:table-cell">Tư vấn</th>
                  <th className="px-3 py-3 text-right pr-4">CV</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.id} onClick={() => setSelected(lead)}
                    className="border-b border-secondary-50 last:border-0 hover:bg-primary-50/40 cursor-pointer align-top">
                    {canAssign && (
                      <td className="pl-3 pr-1 py-3" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={picked.has(lead.id)} onChange={() => togglePick(lead.id)} />
                      </td>
                    )}
                    <td className="px-1 py-3" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => togglePin(lead)} title={lead.is_pinned ? 'Bỏ ghim' : 'Ghim'}
                        className={lead.is_pinned ? 'text-amber-500' : 'text-secondary-300 hover:text-amber-400'}>
                        {lead.is_pinned ? <HiStar className="w-4 h-4" /> : <HiOutlineStar className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-3 py-3 text-xs text-secondary-500 whitespace-nowrap hidden sm:table-cell" title={fmtDateTime(lead.created_at)}>{fmtRelative(lead.created_at)}</td>
                    <td className="px-2 py-3 text-2xs text-secondary-400 font-mono whitespace-nowrap hidden sm:table-cell">#{lead.id}</td>
                    <td className="px-3 py-3">
                      <div className="font-semibold text-secondary-900 leading-tight">{lead.full_name}</div>
                      <a href={`tel:${lead.phone}`} onClick={(e) => e.stopPropagation()} className="text-xs text-primary-600 hover:underline">{lead.phone}</a>
                      {/* Mobile: gộp Thời gian + ID vào đây cho gọn */}
                      <div className="sm:hidden text-2xs text-secondary-400 mt-0.5" title={fmtDateTime(lead.created_at)}>{fmtRelative(lead.created_at)} · #{lead.id}</div>
                      {lead.form && <div className="mt-0.5"><span className="text-2xs bg-violet-50 text-violet-600 px-1.5 py-0.5 rounded truncate inline-block max-w-[150px]">{lead.form.title}</span></div>}
                    </td>
                    <td className="px-3 py-3 hidden md:table-cell">
                      <div className="flex flex-col gap-1 items-start">
                        {lead.source && <span className="text-2xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">{lead.source}</span>}
                        {lead.area && <span title={lead.area} className="text-2xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded truncate max-w-[170px] inline-block">📍 {lead.area}</span>}
                        {lead.position && <span className="text-2xs bg-secondary-100 text-secondary-500 px-1.5 py-0.5 rounded truncate max-w-[170px]">{lead.position}</span>}
                        {!lead.source && !lead.area && !lead.position && <span className="text-secondary-300">—</span>}
                      </div>
                    </td>
                    {canAssign && <td className="px-3 py-3 hidden lg:table-cell"><AssigneeChip name={lead.assignee?.full_name} /></td>}
                    <td className="px-3 py-3"><span className={LEAD_STATUS_BADGE[lead.status] || 'badge-neutral'}>{LEAD_STATUS_LABELS[lead.status] || lead.status}</span></td>
                    <td className="px-3 py-3 hidden lg:table-cell max-w-[220px]">
                      <div className="flex items-start gap-1.5">
                        {lead.note_count > 0 && (
                          <span className="shrink-0 inline-flex items-center gap-0.5 h-5 px-1.5 rounded-full bg-primary-100 text-primary-700 text-2xs font-bold" title={`${lead.note_count} mốc tư vấn`}>💬{lead.note_count}</span>
                        )}
                        <span className="text-secondary-500 text-xs truncate">{lead.admin_note || lead.note || <span className="text-secondary-300">—</span>}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right pr-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        {lead.cv_url
                          ? <button type="button" onClick={() => openCv(lead)} className="text-primary-600 hover:text-primary-700" title={lead.cv_filename || 'Xem CV'}><HiOutlineDocumentText className="w-5 h-5" /></button>
                          : <span className="text-secondary-300">—</span>}
                        {canDeleteLead(lead) && (
                          <button type="button" onClick={() => handleDelete(lead)} className="text-secondary-300 hover:text-red-600" title="Xóa hồ sơ">
                            <HiOutlineTrash className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pagination && pagination.total > 0 && (() => {
          const pages = pagination.total_pages || 1
          return (
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-secondary-100 text-sm">
              <span className="text-secondary-500">
                {(pagination.page - 1) * PER_PAGE + 1}–{Math.min(pagination.page * PER_PAGE, pagination.total)} / <b>{pagination.total}</b> hồ sơ
              </span>
              <div className="flex items-center gap-1">
                <button disabled={page <= 1} onClick={() => setPage(1)} title="Trang đầu"
                  className="px-2 py-1.5 rounded-lg border border-secondary-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-secondary-50">«</button>
                <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-2.5 py-1.5 rounded-lg border border-secondary-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-secondary-50">←</button>
                {pageNumbers(pagination.page, pages).map((n, i) => (
                  n === '…'
                    ? <span key={i} className="px-1 text-secondary-400">…</span>
                    : <button key={i} onClick={() => setPage(n)}
                        className={`min-w-[34px] px-2 py-1.5 rounded-lg border text-sm ${n === pagination.page ? 'bg-primary-600 text-white border-primary-600' : 'border-secondary-200 hover:bg-secondary-50'}`}>{n}</button>
                ))}
                <button disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  className="px-2.5 py-1.5 rounded-lg border border-secondary-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-secondary-50">→</button>
                <button disabled={page >= pages} onClick={() => setPage(pages)} title="Trang cuối"
                  className="px-2 py-1.5 rounded-lg border border-secondary-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-secondary-50">»</button>
                {pages > 5 && (
                  <span className="flex items-center gap-1 ml-2 text-xs text-secondary-500">
                    Tới
                    <input type="number" min="1" max={pages} defaultValue={pagination.page} key={pagination.page}
                      onKeyDown={(e) => { if (e.key === 'Enter') setPage(Math.min(pages, Math.max(1, parseInt(e.target.value, 10) || 1))) }}
                      className="input w-16 text-xs py-1 text-center" title="Nhập số trang rồi Enter" />
                    /{pages}
                  </span>
                )}
              </div>
            </div>
          )
        })()}
      </div>

      {selected && (
        <LeadDetailModal
          lead={selected} canAssign={canAssign} isAdmin={isAdmin} canDelete={canDeleteLead(selected)} assignables={assignables}
          onClose={() => setSelected(null)} onSaved={() => { setSelected(null); load() }} onDelete={handleDelete}
          onTogglePin={() => togglePin(selected)}
        />
      )}
    </div>
  )
}

// Nội dung bình luận có "xem thêm" nếu dài
function CommentContent({ text }) {
  const [expanded, setExpanded] = useState(false)
  if (!text) return null
  const long = text.length > 180
  return (
    <div className="text-sm text-secondary-800 whitespace-pre-wrap break-words">
      {long && !expanded ? text.slice(0, 180) + '… ' : text}
      {long && <button onClick={() => setExpanded((v) => !v)} className="text-primary-600 text-xs font-medium ml-0.5">{expanded ? 'Thu gọn' : 'Xem thêm'}</button>}
    </div>
  )
}

// 1 mục timeline: note/call = bình luận (avatar + tên + giờ + nội dung); status/assign/region = dòng sự kiện nhỏ
function TimelineItem({ a }) {
  const isEvent = a.type === 'status' || a.type === 'assign' || a.type === 'region'
  if (isEvent) {
    const label = a.type === 'status' ? (LEAD_STATUS_LABELS[a.content] || a.content) : a.content
    return (
      <div className="flex items-center flex-wrap gap-x-1.5 text-2xs text-secondary-400 pl-1 py-0.5">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
        <span className="font-medium text-secondary-500">{a.user_name || 'Hệ thống'}</span>
        <span>{ACTIVITY_LABELS[a.type] || a.type}: {label}</span>
        <span>· {fmtRelative(a.created_at)}</span>
      </div>
    )
  }
  return (
    <div className="flex gap-2.5">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${chipColor(a.user_name || 'H')}`}>
        {(a.user_name || 'H').charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0 bg-white rounded-lg border border-secondary-100 p-2.5">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <span className="text-sm font-medium text-secondary-800">{a.user_name || 'Hệ thống'}</span>
          <span className="text-2xs text-secondary-400" title={fmtDateTime(a.created_at)}>{fmtRelative(a.created_at)}</span>
        </div>
        {a.outcome && <span className="inline-block mb-1 text-2xs font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">{CALL_OUTCOME_LABELS[a.outcome] || a.outcome}</span>}
        <CommentContent text={a.content} />
        {a.next_action_at && <div className="text-2xs text-amber-600 mt-1">⏰ Hẹn: {fmtDateTime(a.next_action_at)}</div>}
      </div>
    </div>
  )
}

function LeadDetailModal({ lead, canAssign, isAdmin, canDelete, assignables, onClose, onSaved, onDelete, onTogglePin }) {
  const [status, setStatus] = useState(lead.status)
  const [adminNote, setAdminNote] = useState(lead.admin_note || '')
  const [assignedTo, setAssignedTo] = useState(lead.assigned_to || '')
  const [region, setRegion] = useState(lead.region || lead.area || '')
  const [saving, setSaving] = useState(false)
  const [acts, setActs] = useState([])
  const [clog, setClog] = useState('')
  const [clogging, setClogging] = useState(false)

  const loadActs = useCallback(() => {
    leadService.activities(lead.id).then((r) => { if (r.status === 'success') setActs(r.data) }).catch(() => {})
  }, [lead.id])
  useEffect(() => { loadActs() }, [loadActs])

  const save = async () => {
    setSaving(true)
    try {
      const payload = { status, admin_note: adminNote }
      if (canAssign) { payload.assigned_to = assignedTo === '' ? null : Number(assignedTo); payload.region = region }
      const res = await leadService.update(lead.id, payload)
      if (res.status === 'success') { toast.success('Đã cập nhật hồ sơ'); onSaved() }
    } catch { /* interceptor */ } finally { setSaving(false) }
  }

  const addCall = async () => {
    const content = clog.trim()
    if (!content) { toast.error('Nhập nội dung tư vấn'); return }
    setClogging(true)
    try {
      const res = await leadService.addContactLog(lead.id, { content })   // thời gian = hiện tại (server)
      if (res.status === 'success') {
        if (res.data?.activities) setActs(res.data.activities)
        setAdminNote(content)
        setClog('')
        toast.success('Đã ghi mốc tư vấn')
      }
    } catch { /* interceptor */ } finally { setClogging(false) }
  }

  const Row = ({ label, children }) => (
    <div className="py-1.5">
      <div className="text-2xs uppercase tracking-wide text-secondary-400 mb-0.5">{label}</div>
      <div className="text-sm text-secondary-800 break-words">{children || '—'}</div>
    </div>
  )

  const notes = acts.filter((a) => a.type === 'call' || a.type === 'note')

  return (
    <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center sm:p-6">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full sm:max-w-4xl bg-white sm:rounded-2xl shadow-2xl h-full sm:h-auto sm:max-h-[92vh] flex flex-col overflow-hidden sm:animate-scale-in">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-4 sm:px-6 py-4 border-b border-secondary-100">
          <button onClick={onClose} className="sm:hidden p-1 -ml-1 text-secondary-500 shrink-0 self-center" title="Quay lại">
            <HiOutlineArrowLeft className="w-6 h-6" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-lg sm:text-xl font-bold text-secondary-900 truncate">{lead.full_name}</h3>
              <button onClick={onTogglePin} title={lead.is_pinned ? 'Bỏ ghim' : 'Ghim'} className={lead.is_pinned ? 'text-amber-500' : 'text-secondary-300 hover:text-amber-400'}>
                {lead.is_pinned ? <HiStar className="w-5 h-5" /> : <HiOutlineStar className="w-5 h-5" />}
              </button>
              <span className="text-2xs text-secondary-400 font-mono">#{lead.id}</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm">
              <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-1 text-primary-600"><HiOutlinePhone className="w-4 h-4" /> {lead.phone}</a>
              {lead.email && <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1 text-primary-600"><HiOutlineMail className="w-4 h-4" /> {lead.email}</a>}
              <span className={LEAD_STATUS_BADGE[lead.status] || 'badge-neutral'}>{LEAD_STATUS_LABELS[lead.status] || lead.status}</span>
            </div>
          </div>
          <button onClick={onClose} className="hidden sm:block p-1.5 text-secondary-400 hover:text-secondary-700 shrink-0"><HiOutlineX className="w-6 h-6" /></button>
        </div>

        {/* Body 2 cột */}
        <div className="flex-1 overflow-y-auto grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-secondary-100">
          {/* Cột trái: thông tin + xử lý */}
          <div className="p-5 sm:p-6">
            <div className="grid grid-cols-2 gap-x-4">
              <Row label="Năm sinh">{lead.birth_year}</Row>
              <Row label="Nơi ở hiện tại">{lead.current_location}</Row>
              <Row label="KV mong muốn">{lead.desired_area}</Row>
              <Row label="Vị trí ứng tuyển">{lead.position}</Row>
              <Row label="Kinh nghiệm">{lead.experience}</Row>
              <Row label="Nguồn">{lead.source}</Row>
              <Row label="Khu vực">{lead.area}</Row>
              <Row label="Chiến dịch">{lead.form ? lead.form.title : 'Trang chủ'}</Row>
            </div>
            {lead.extra_data && Object.keys(lead.extra_data).length > 0 && (
              <div className="mt-2 pt-2 border-t border-secondary-50 grid grid-cols-2 gap-x-4">
                {Object.entries(lead.extra_data).map(([k, v]) => (
                  <Row key={k} label={k}>
                    {typeof v === 'string' && /^https?:\/\//.test(v)
                      ? <a href={v} target="_blank" rel="noreferrer" className="text-primary-600 break-all">Xem</a>
                      : Array.isArray(v) ? v.join(', ') : String(v)}
                  </Row>
                ))}
              </div>
            )}
            <div className="mt-2 pt-2 border-t border-secondary-50">
              <Row label="CV">
                {lead.cv_url ? <button type="button" onClick={() => openCv(lead)} className="inline-flex items-center gap-1 text-primary-600"><HiOutlineDocumentText className="w-4 h-4" /> {lead.cv_filename || 'Mở CV'}</button> : '—'}
              </Row>
              {lead.note && <Row label="Ghi chú ứng viên">{lead.note}</Row>}
            </div>

            {/* Xử lý */}
            <div className="mt-4 pt-4 border-t border-secondary-100 space-y-3">
              <div>
                <label className="label">Trạng thái xử lý</label>
                <StatusSelect value={status} onChange={(e) => setStatus(e.target.value)} />
              </div>
              {canAssign && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="label">Phân công cho</label>
                    <select className="select" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
                      <option value="">-- Chưa phân công --</option>
                      {assignables.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label">Khu vực</label>
                    <input className="input" value={region} onChange={(e) => setRegion(e.target.value)} placeholder="VD: Đà Nẵng" />
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <button onClick={save} disabled={saving} className="btn-primary flex-1">{saving ? 'Đang lưu…' : 'Lưu thay đổi'}</button>
                {canDelete && <button onClick={() => onDelete(lead)} className="btn-danger" title="Xoá hồ sơ"><HiOutlineTrash className="w-5 h-5" /></button>}
              </div>
            </div>
          </div>

          {/* Cột phải: timeline bình luận */}
          <div className="p-5 sm:p-6 bg-secondary-50/40 flex flex-col">
            <div className="flex items-center gap-1.5 mb-3 font-medium text-secondary-700">
              <HiOutlineClock className="w-4 h-4" /> Timeline tư vấn <span className="text-2xs text-secondary-400">({notes.length})</span>
            </div>
            <div className="mb-4">
              <textarea className="input min-h-[60px] text-sm" value={clog}
                onChange={(e) => setClog(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) addCall() }}
                placeholder="Viết bình luận tư vấn… (Ctrl+Enter để gửi)" />
              <button onClick={addCall} disabled={clogging} className="btn-primary w-full text-sm py-2 mt-2">
                {clogging ? 'Đang gửi…' : '+ Thêm mốc tư vấn'}
              </button>
            </div>
            {acts.length === 0 ? (
              <p className="text-xs text-secondary-400 text-center py-6">Chưa có hoạt động nào. Hãy thêm mốc tư vấn đầu tiên.</p>
            ) : (
              <div className="space-y-3">
                {acts.map((a) => <TimelineItem key={a.id} a={a} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default LeadManagement
